import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditNoteIcon from '@mui/icons-material/EditNote';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import StorageIcon from '@mui/icons-material/Storage';
import PeopleIcon from '@mui/icons-material/People';
import SaveIcon from '@mui/icons-material/Save';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Paper,
    Stack,
    IconButton,
    Divider,
    Chip,
    Alert,
    Skeleton,
    TextField,
    Switch,
    FormControlLabel
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { httpClient } from '../../../common/httpclient';
import { planSegmentLabel } from '../../../common/planSegments';
import { planDisplayName } from '../../../common/planDisplayName';
import PageScaffold from '../../../component/pageScaffold/PageScaffold';
import { getModuleIcon } from './planModuleIcons';

const MAX_MODULE_DESCRIPTION = 300;

// Monta o estado do formulário de itens a partir da resposta do backend. As flags
// dos módulos seguem `modules_catalog` (fonte única no backend): cada módulo vira
// um campo `mod_*` booleano; as descrições ficam em `module_descriptions[key]`.
const buildItemsForm = (items) => {
    const form = {
        max_data_mb: items?.max_data_mb || null,
        max_users: items?.max_users || null,
        module_descriptions: { ...(items?.module_descriptions || {}) }
    };
    (items?.modules_catalog || []).forEach((mod) => {
        form[mod.field] = Boolean(items?.[mod.field]);
    });
    return form;
};

export default function PlanView() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [planData, setPlanData] = useState(null);    const [planItems, setPlanItems] = useState(null);
    const [error, setError] = useState('');
    const [editItemsMode, setEditItemsMode] = useState(false);
    const [savingItems, setSavingItems] = useState(false);
    const [itemsFormData, setItemsFormData] = useState(buildItemsForm(null));
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const modulesCatalog = planItems?.modules_catalog || [];

    const formatCurrency = (valueReais, valueCents) => {
        const totalValue = parseFloat(valueReais || 0) + (parseFloat(valueCents || 0) / 100);
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(totalValue);
    };

    const formatBytes = (mb) => {
        if (!mb) return 'Ilimitado';
        return `${mb.toLocaleString('pt-BR')} MB`;
    };

    const fetchPlanData = async () => {
        try {
            setLoading(true);
            const [planResponse, itemsResponse] = await Promise.all([
                httpClient.get(`/admin/plans/${id}`),
                httpClient.get(`/admin/plans/${id}/items`)
            ]);

            setPlanData(planResponse.data);
            setPlanItems(itemsResponse.data);
            // Initialize form data with fetched items
            if (itemsResponse.data) {
                setItemsFormData(buildItemsForm(itemsResponse.data));
            }
        } catch (error) {
            console.error('Error fetching plan data:', error);
            setError('Erro ao carregar dados do plano');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchPlanData();
        }
    }, [id]);

    const handleEditItemsToggle = () => {
        // Reset form data to original values when canceling (if currently in edit mode)
        if (editItemsMode && planItems) {
            setItemsFormData(buildItemsForm(planItems));
        }
        setEditItemsMode(!editItemsMode);
    };

    const handleItemsInputChange = (field, value) => {
        setItemsFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleModuleDescriptionChange = (key, value) => {
        setItemsFormData(prev => ({
            ...prev,
            module_descriptions: { ...prev.module_descriptions, [key]: value }
        }));
    };

    const handleSaveItems = async () => {
        try {
            setSavingItems(true);
            const payload = {
                max_data_mb: itemsFormData.max_data_mb !== null && itemsFormData.max_data_mb !== undefined
                    ? parseInt(itemsFormData.max_data_mb)
                    : null,
                max_users: itemsFormData.max_users !== null && itemsFormData.max_users !== undefined
                    ? parseInt(itemsFormData.max_users)
                    : null,
                module_descriptions: itemsFormData.module_descriptions
            };
            modulesCatalog.forEach((mod) => {
                payload[mod.field] = Boolean(itemsFormData[mod.field]);
            });

            const response = await httpClient.put(`/admin/plans/${id}/items`, payload);

            if (response.status === 200 || response.status === 204) {
                setSnackbar({
                    open: true,
                    message: 'Itens do plano atualizados com sucesso!',
                    severity: 'success'
                });
                setEditItemsMode(false);
                // Reload plan items
                await fetchPlanData();
            } else {
                setSnackbar({
                    open: true,
                    message: response.data?.message || 'Erro ao atualizar itens do plano',
                    severity: 'error'
                });
            }
        } catch (error) {
            console.error('Error updating plan items:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.id || 'Erro ao atualizar itens do plano';
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        } finally {
            setSavingItems(false);
        }
    };

    const breadcrumbs = [
        {
            title: 'Planos',
            link: '/admin/plans'
        }
    ];

    // Componente Skeleton
    const ViewSkeleton = () => (
        <Box sx={{ p: 2 }}>
            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    borderRadius: 3,
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: theme.palette.mode === 'dark' ? 'text.primary' : 'white',
                    border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Box>
                            <Skeleton variant="text" width={350} height={48} />
                            <Skeleton variant="text" width={280} height={20} />
                        </Box>
                    </Stack>
                </Box>
            </Paper>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
                    <Card>
                        <CardContent>
                            <Skeleton variant="text" width="60%" height={32} />
                            <Skeleton variant="text" width="100%" height={56} />
                            <Skeleton variant="text" width="100%" height={56} />
                        </CardContent>
                    </Card>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
                    <Card>
                        <CardContent>
                            <Skeleton variant="text" width="60%" height={32} />
                            <Skeleton variant="text" width="100%" height={56} />
                            <Skeleton variant="text" width="100%" height={56} />
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </Box>
    );

    if (loading) {
        return (
            <PageScaffold
                links={breadcrumbs}
                titlePageTarget="Visualizar Plano"
            >
                <ViewSkeleton />
            </PageScaffold>
        );
    }

    if (error || !planData) {
        return (
            <PageScaffold
                links={breadcrumbs}
                titlePageTarget="Visualizar Plano"
            >
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || 'Plano não encontrado'}
                </Alert>
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={breadcrumbs}
            titlePageTarget="Visualizar Plano"
            openSnackbar={snackbar.open}
            setOpenSnackbar={(open) => setSnackbar(prev => ({ ...prev, open }))}
            severity={snackbar.severity}
            message={snackbar.message}
        >
            <Box sx={{ p: 2 }}>
                {/* Header Section */}
                <Paper
                    elevation={0}
                    sx={{
                        mb: 3,
                        borderRadius: 3,
                        background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: theme.palette.mode === 'dark' ? 'text.primary' : 'white',
                        border: theme.palette.mode === 'dark'
                            ? '1px solid rgba(255,255,255,0.1)'
                            : 'none',
                    }}
                >
                    <Box sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <IconButton
                                    onClick={() => navigate('/admin/plans')}
                                    sx={{
                                        color: theme.palette.mode === 'dark' ? 'text.primary' : 'white',
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.05)'
                                            : 'rgba(255,255,255,0.1)',
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? 'rgba(255,255,255,0.1)'
                                                : 'rgba(255,255,255,0.2)'
                                        }
                                    }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                                        {planDisplayName(planData.name)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Visualização detalhada do plano
                                    </Typography>
                                </Box>
                            </Stack>
                            <Stack direction="row" spacing={2}>
                                <Chip
                                    icon={planData.is_visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                    label={planData.is_visible ? 'Visível' : 'Oculto'}
                                    color={planData.is_visible ? 'success' : 'default'}
                                    sx={{
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.1)'
                                            : 'rgba(255,255,255,0.2)',
                                        color: theme.palette.mode === 'dark' ? 'text.primary' : 'white',
                                    }}
                                />
                                <Chip
                                    label={planSegmentLabel(planData.segment)}
                                    sx={{
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.1)'
                                            : 'rgba(255,255,255,0.2)',
                                        color: theme.palette.mode === 'dark' ? 'text.primary' : 'white',
                                    }}
                                />
                                <Chip
                                    label={planData.show_in_catalog ? 'Na landing' : 'Customizado'}
                                    color={planData.show_in_catalog ? 'success' : 'default'}
                                    sx={{
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.1)'
                                            : 'rgba(255,255,255,0.2)',
                                        color: theme.palette.mode === 'dark' ? 'text.primary' : 'white',
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={<EditNoteIcon />}
                                    onClick={() => navigate(`/admin/plans/edit/${id}`)}
                                    sx={{
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.2)'
                                            : 'rgba(255,255,255,0.9)',
                                        color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.main',
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? 'rgba(255,255,255,0.3)'
                                                : 'white',
                                        }
                                    }}
                                >
                                    Editar
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </Paper>

                {/* Main Content */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    {/* Informações Básicas */}
                    <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
                        <Card
                            elevation={2}
                            sx={{
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: theme.palette.divider,
                                height: '100%'
                            }}
                        >
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                                    <WorkspacePremiumIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Informações Básicas
                                    </Typography>
                                </Stack>

                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Nome do Plano
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                                            {planDisplayName(planData.name)}
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Descrição
                                        </Typography>
                                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                                            {planData.description || 'Sem descrição'}
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Referência Externa
                                        </Typography>
                                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                                            {planData.external_reference || '-'}
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Tokens de IA / Mensagens de WhatsApp por mês
                                        </Typography>
                                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                                            {(planData.ai_tokens || 0).toLocaleString('pt-BR')} / {(planData.whatsapp_messages || 0).toLocaleString('pt-BR')}
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Link de pagamento
                                        </Typography>
                                        <Typography variant="body1" sx={{ mt: 0.5, wordBreak: 'break-all' }}>
                                            {planData.payment_link_url || '-'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Valor */}
                    <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
                        <Card
                            elevation={2}
                            sx={{
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: theme.palette.divider,
                                height: '100%'
                            }}
                        >
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                                    <MonetizationOnIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Valor do Plano
                                    </Typography>
                                </Stack>

                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 3,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'rgba(0,0,0,0.02)',
                                    border: '1px dashed',
                                    borderColor: theme.palette.divider
                                }}>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 700,
                                            color: 'success.main'
                                        }}
                                    >
                                        {formatCurrency(planData.value_reais, planData.value_cents)}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Itens do Plano */}
                    <Box sx={{ flex: '1 1 100%' }}>
                        <Card
                            elevation={2}
                            sx={{
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: theme.palette.divider
                            }}
                        >
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between" sx={{ mb: 3 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <SettingsIcon color="primary" />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Itens e Recursos do Plano
                                        </Typography>
                                    </Stack>
                                    {!editItemsMode ? (
                                        <Button
                                            variant="outlined"
                                            startIcon={<EditNoteIcon />}
                                            onClick={handleEditItemsToggle}
                                            size="small"
                                        >
                                            Editar Itens
                                        </Button>
                                    ) : (
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                variant="contained"
                                                startIcon={<SaveIcon />}
                                                onClick={handleSaveItems}
                                                disabled={savingItems}
                                                size="small"
                                                color="primary"
                                            >
                                                Salvar
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                startIcon={<CancelIcon />}
                                                onClick={handleEditItemsToggle}
                                                disabled={savingItems}
                                                size="small"
                                            >
                                                Cancelar
                                            </Button>
                                        </Stack>
                                    )}
                                </Stack>

                                {planItems ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                        {/* Max Data MB */}
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' } }}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    backgroundColor: theme.palette.mode === 'dark'
                                                        ? 'rgba(255,255,255,0.03)'
                                                        : 'grey.50',
                                                    border: '1px solid',
                                                    borderColor: theme.palette.divider
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                    <StorageIcon color="primary" fontSize="small" />
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        Dados (MB)
                                                    </Typography>
                                                </Stack>
                                                {editItemsMode ? (
                                                    <TextField
                                                        type="number"
                                                        value={itemsFormData.max_data_mb || ''}
                                                        onChange={(e) => handleItemsInputChange('max_data_mb', e.target.value ? parseInt(e.target.value) : null)}
                                                        placeholder="Ilimitado"
                                                        fullWidth
                                                        size="small"
                                                        inputProps={{ min: 0 }}
                                                        helperText="Deixe vazio para ilimitado"
                                                    />
                                                ) : (
                                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                        {formatBytes(planItems.max_data_mb)}
                                                    </Typography>
                                                )}
                                            </Paper>
                                        </Box>

                                        {/* Max Users */}
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(33.333% - 16px)' } }}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    backgroundColor: theme.palette.mode === 'dark'
                                                        ? 'rgba(255,255,255,0.03)'
                                                        : 'grey.50',
                                                    border: '1px solid',
                                                    borderColor: theme.palette.divider
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                    <PeopleIcon color="primary" fontSize="small" />
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        Usuários Máximos
                                                    </Typography>
                                                </Stack>
                                                {editItemsMode ? (
                                                    <TextField
                                                        type="number"
                                                        value={itemsFormData.max_users || ''}
                                                        onChange={(e) => handleItemsInputChange('max_users', e.target.value ? parseInt(e.target.value) : null)}
                                                        placeholder="Ilimitado"
                                                        fullWidth
                                                        size="small"
                                                        inputProps={{ min: 0 }}
                                                        helperText="Deixe vazio para ilimitado"
                                                    />
                                                ) : (
                                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                        {planItems.max_users ? planItems.max_users.toLocaleString('pt-BR') : 'Ilimitado'}
                                                    </Typography>
                                                )}
                                            </Paper>
                                        </Box>

                                        {/* Módulos: lista vem de planItems.modules_catalog (fonte única no backend) */}
                                        <Box sx={{ flex: '1 1 100%' }}>
                                            <Divider sx={{ my: 2 }} />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                Módulos Disponíveis
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                A descrição de cada módulo aparece no catálogo de planos da landing. Módulos ligados sempre aparecem no catálogo; a descrição é opcional.
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                                {modulesCatalog.map((mod) => {
                                                    const ModuleIcon = getModuleIcon(mod.key);
                                                    const customDescription = (planItems.module_descriptions || {})[mod.key];
                                                    const formDescription = itemsFormData.module_descriptions[mod.key] || '';
                                                    return (
                                                        <Box
                                                            key={mod.key}
                                                            sx={{
                                                                flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.333% - 11px)' },
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 1,
                                                                p: 1.5,
                                                                borderRadius: 1,
                                                                backgroundColor: theme.palette.mode === 'dark'
                                                                    ? 'rgba(255,255,255,0.03)'
                                                                    : 'grey.50'
                                                            }}
                                                        >
                                                            {editItemsMode ? (
                                                                <>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={Boolean(itemsFormData[mod.field])}
                                                                                onChange={(e) => handleItemsInputChange(mod.field, e.target.checked)}
                                                                                size="small"
                                                                            />
                                                                        }
                                                                        label={
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <ModuleIcon fontSize="small" color="action" />
                                                                                <Typography variant="body2">{mod.label}</Typography>
                                                                            </Box>
                                                                        }
                                                                        sx={{ m: 0 }}
                                                                    />
                                                                    <TextField
                                                                        value={formDescription}
                                                                        onChange={(e) => handleModuleDescriptionChange(mod.key, e.target.value)}
                                                                        placeholder={mod.default_description}
                                                                        multiline
                                                                        minRows={2}
                                                                        fullWidth
                                                                        size="small"
                                                                        inputProps={{ maxLength: MAX_MODULE_DESCRIPTION }}
                                                                        helperText={`${formDescription.length}/${MAX_MODULE_DESCRIPTION}`}
                                                                    />
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        {planItems[mod.field] ? (
                                                                            <CheckCircleIcon color="success" fontSize="small" />
                                                                        ) : (
                                                                            <CancelIcon color="error" fontSize="small" />
                                                                        )}
                                                                        <ModuleIcon fontSize="small" color="action" />
                                                                        <Typography variant="body2">
                                                                            {mod.label}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {customDescription || 'Sem descrição'}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Alert severity="info">
                                        Nenhum item configurado para este plano
                                    </Alert>
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            </Box>
        </PageScaffold>
    );
}
